import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';

const BannerQueueContext = createContext(null);

export const BannerQueueProvider = ({ children }) => {
  const [registrations, setRegistrations] = useState({});

  const registerBanner = useCallback((key, priority, isEligible) => {
    setRegistrations(prev => {
      const existing = prev[key];
      if (existing && existing.priority === priority && existing.isEligible === isEligible) {
        return prev;
      }
      return {
        ...prev,
        [key]: { priority, isEligible }
      };
    });
  }, []);

  const unregisterBanner = useCallback((key) => {
    setRegistrations(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const activeBannerKey = useMemo(() => {
    let winningKey = null;
    let highestPriority = Infinity;

    for (const [key, config] of Object.entries(registrations)) {
      if (config.isEligible && config.priority < highestPriority) {
        highestPriority = config.priority;
        winningKey = key;
      }
    }
    return winningKey;
  }, [registrations]);

  const value = useMemo(() => ({
    registerBanner,
    unregisterBanner,
    activeBannerKey,
  }), [registerBanner, unregisterBanner, activeBannerKey]);

  return (
    <BannerQueueContext.Provider value={value}>
      {children}
    </BannerQueueContext.Provider>
  );
};

export const useBannerQueue = () => {
  const ctx = useContext(BannerQueueContext);
  return ctx || { registerBanner: () => {}, unregisterBanner: () => {}, activeBannerKey: null };
};

export const useRegisterBanner = (key, priority, isEligible) => {
  const { registerBanner, unregisterBanner, activeBannerKey } = useBannerQueue();

  useEffect(() => {
    registerBanner(key, priority, Boolean(isEligible));
  }, [key, priority, isEligible, registerBanner]);

  useEffect(() => {
    return () => {
      unregisterBanner(key);
    };
  }, [key, unregisterBanner]);

  return {
    isActive: activeBannerKey === key,
    activeBannerKey,
  };
};

export default BannerQueueContext;
